'use client';

import { useQuery } from '@tanstack/react-query';
import type { Editor } from '@tiptap/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { mentionPluginKey, useEditorPopover } from '@zoomit/editor';
import { Breakpoints, Flex } from '@zoomit/primitives';
import { BaseMediaUrl } from '@zoomit/web-kit';
import { Avatar, cssVar, elevation, theme, Typography } from '@zoomit/zoomit-ds';

import { searchUser } from '../../../apis/userInfo/search';

type Props = {
  editor: Editor;
};

export const MentionList = (props: Props) => {
  const { editor } = props;
  const [focusIndex, setFocusIndex] = useState(0);

  const mentionState = useMemo(() => mentionPluginKey.getState(editor.state), [editor.state]);

  const { refs, floatingStyles } = useEditorPopover(editor);

  const scrollHandlerRef = useRef<HTMLDivElement>(null);

  const { data, isLoading, isSuccess } = useQuery({
    queryKey: ['mention', mentionState?.query],
    queryFn: async () => {
      if (!mentionState?.query) return [];
      return (await searchUser(mentionState.query, 10)).data.source;
    },
    onSuccess: () => {
      setFocusIndex(0);
    },
  });

  const selectItem = useCallback(
    (index: number) => {
      if (!mentionState?.range || !data || data.length === 0) return;

      const range = { ...mentionState?.range };
      const nodeAfter = editor.view.state.selection.$to.nodeAfter;
      const overrideSpace = nodeAfter?.text?.startsWith(' ');

      if (overrideSpace) {
        range.to += 1;
      }

      editor
        .chain()
        .focus()
        .insertContentAt(range, [
          {
            type: 'mention',
            attrs: { id: data[index].id, label: data[index].userName },
          },
          {
            type: 'text',
            text: ' ',
          },
        ])
        .run();

      window.getSelection()?.collapseToEnd();
    },
    [data, editor, mentionState?.range],
  );

  useEffect(() => {
    if (!data) return;
    if (!scrollHandlerRef.current) return;
    if (!mentionState || !mentionState?.active) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown': {
          const next = focusIndex === data.length - 1 ? 0 : focusIndex + 1;
          scrollHandlerRef.current?.querySelector(`#mention-item-${next}`)?.scrollIntoView({
            block: 'nearest',
          });
          setFocusIndex(next);
          break;
        }
        case 'ArrowUp': {
          const next = focusIndex === 0 ? data.length - 1 : focusIndex - 1;
          scrollHandlerRef.current?.querySelector(`#mention-item-${next}`)?.scrollIntoView({
            block: 'nearest',
          });
          setFocusIndex(next);
          break;
        }
        case 'Enter':
          selectItem(focusIndex);
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [data, focusIndex, mentionState, selectItem]);

  if (!mentionState || !mentionState?.active) return null;

  return (
    <MentionBox
      ref={refs.setFloating}
      style={floatingStyles}
      flexDirection="column"
      gap={1}
      spacing={{ p: 'x4' }}
    >
      <div ref={scrollHandlerRef}>
        {mentionState?.query === '' && (
          <Typography textColor={cssVar(theme.grey1)}>نام یا نام کاربری را بنویسید</Typography>
        )}
        {isLoading && <Typography textColor={cssVar(theme.grey1)}>در حال جستجو ...</Typography>}
        {mentionState?.query !== '' && isSuccess && data.length === 0 && (
          <Typography textColor={cssVar(theme.grey1)}>کاربری یافت نشد</Typography>
        )}
        {isSuccess &&
          mentionState?.query !== '' &&
          data.map((item, index) => (
            <MentionItem
              id={'mention-item-' + index}
              onClick={() => selectItem(index)}
              focus={index === focusIndex}
              flexWrap="nowrap"
              gap={0.8}
              alignItems="center"
            >
              <Avatar src={`${BaseMediaUrl}/${item.avatarId}`} />
              <Typography fontWeight={700}>{item.userName}</Typography>
              <EllipsisText variant="subhead" textColor={cssVar(theme.grey1)}>
                {item.fullName}
              </EllipsisText>
            </MentionItem>
          ))}
      </div>
    </MentionBox>
  );
};

const MentionBox = styled(Flex)`
  z-index: 10;
  background-color: var(${theme.card});
  overflow-y: auto;
  ${elevation}
  border-radius: ${cssVar(theme.radius.md)};

  max-height: 184px;
  @media (min-width: ${Breakpoints.md}) {
    max-height: 260px;
    width: 360px;
  }
`;

const MentionItem = styled(Flex)<{ focus: boolean }>`
  padding: 0.4rem;
  border-radius: ${cssVar(theme.radius.md)};
  cursor: pointer;
  background-color: ${({ focus }) => (focus ? cssVar(theme.grey6) : 'transparent')};
  border: ${({ focus }) => (focus ? ` 1px solid var(${theme.grey7})` : 'unset')};
  &:hover {
    background-color: var(${theme.grey7});
  }
`;

const EllipsisText = styled(Typography)`
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;
  overflow: hidden;
`;
